import type { ProcessModel } from '@models/process.model';
import { AlertTriangle, ChevronRight } from 'lucide-react';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { ParamsList } from '@/routes/routes.types';

type WorkflowListCardProps = {
  process: ProcessModel;
};

export const WorkflowListCard = observer(
  ({ process }: WorkflowListCardProps) => {
    const navigate = useNavigate();
    const processId = useParams()[ParamsList.ProcessId];
    if (!processId) {
      throw new Error('Missing processId route param');
    }
    const workflows = process.workflows;

    if (workflows.length === 0) {
      return (
        <p className="mt-4 text-muted-foreground text-sm">
          No workflows in this process yet.
        </p>
      );
    }

    return (
      <div className="mt-4 flex flex-col gap-2">
        <h4 className="flex items-center gap-2 font-medium text-sm">
          Existing Workflows
          <Badge variant="secondary">{workflows.length}</Badge>
        </h4>
        <div className="flex flex-col gap-2">
          {workflows.map(workflow => (
            <button
              className="flex w-full cursor-pointer items-center gap-4 rounded-lg border bg-background p-3 text-left transition-colors hover:bg-muted/50 hover:transition-none"
              key={workflow.id}
              onClick={() => navigate(`/${processId}/${workflow.id}/admin`)}
              type="button"
            >
              <div className="flex flex-1 flex-col gap-1">
                <span className="font-medium text-sm">{workflow.name}</span>
                <div className="flex items-center gap-4 text-muted-foreground text-xs">
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground/70">Env var:</span>
                    {workflow.id_environment_variable_name ? (
                      <code className="rounded bg-muted px-1 font-mono">
                        {workflow.id_environment_variable_name}
                      </code>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-600">
                        <AlertTriangle className="size-3" />
                        Not set
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="text-muted-foreground/70">
                      Published ID:
                    </span>
                    {workflow.published_id ? (
                      <code className="max-w-[200px] truncate rounded bg-muted px-1 font-mono">
                        {workflow.published_id}
                      </code>
                    ) : (
                      <span className="italic">None</span>
                    )}
                  </span>
                </div>
              </div>
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
          ))}
        </div>
      </div>
    );
  }
);
